import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    title: "Flight Masters",
    subtitle: "Agencies, airlines and airports used across bookings",
    items: [
       { label: "Flight Details", desc: "Manage flight details", icon: "bi-airplane-fill", tint: "var(--btn-bg)", bg: "#eeecff", to: "/flight/list" },
      { label: "Airline", desc: "Carriers and airline codes", icon: "bi-airplane-fill", tint: "var(--btn-bg)", bg: "#e7f0ff", to: "/airlines-details" },
      { label: "Airport", desc: "Airports and IATA codes", icon: "bi-airplane-engines-fill", tint: "#0ea5e9", bg: "#e0f4fd", to: "/airports-details" },
      //{ label: "Bank Details", desc: "Manage bank accounts for payouts", icon: "bi-bank", tint: "#16a34a", bg: "#e6f6ec", to: "/bank-details" },
      //{ label: "Enquiries Details", desc: "Manage all enquiries", icon: "bi bi-chat-dots-fill", tint: "#16a34a", bg: "#e6f6ec", to: "/enquiries" },
      //{ label: "Promotion", desc: "Manage your promotions", icon: "bi bi-megaphone", tint: "#16a34a", bg: "#e6f6ec", to: "/promotions" },
    ],
  },
  {
    title: "Enquiries & Promotion Masters",
    subtitle: "Agencies, airlines and airports used across bookings",
    items: [            
      { label: "Enquiries Details", desc: "Manage all enquiries", icon: "bi bi-chat-dots-fill", tint: "#16a34a", bg: "#e6f6ec", to: "/enquiries" },
      { label: "Promotion", desc: "Manage your promotions", icon: "bi bi-megaphone", tint: "#16a34a", bg: "#e6f6ec", to: "/promotions" },
      { label: "Home Banner", desc: "Manage your banner images", icon: "bi bi-image", tint: "#16a34a", bg: "#e6f6ec", to: "/offers" },
    ],
  },
  {
    title: "Location Master",
    subtitle: "Countries, states and cities for addresses and filters",
    items: [
      { label: "Country", desc: "Countries and phone codes", icon: "bi-globe", to: "/location/countries" },
      { label: "State", desc: "States by country", icon: "bi-map", to: "/location/states" },
      { label: "City", desc: "Cities by state", icon: "bi-building", to: "/location/cities" },
    ],
  },
  {
    title: "Inventory Configuration",
    subtitle: "Search and fare rules for flight APIs",
    items: [
      { label: "Supplier Inventory Blackout", desc: "Block or allow supplier inventory", icon: "bi-funnel", to: "/supplier-inventory-blackout" },
      { label: "Supplier Inventory Priority", desc: "Set priority for supplier inventory", icon: "bi-sort-numeric-down", to: "/supplier-inventory-priority" },
      { label: "Flight Markup", desc: "Configure flight markups", icon: "bi-cash-stack", to: "/flight-markups" },
      // { label: "Airline Search Restrictions", desc: "Block or allow airlines", icon: "bi-shield-check", to: null },
      // { label: "Flight Api Fare Types", desc: "Configure fare types", icon: "bi-tags", to: null },
    ],
  },
];

const SettingTile = ({ item }) => {
  const disabled = !item.to;
  const content = (
    <>
      <span className="st-tile-icon" aria-hidden="true">
        <i className={`bi ${item.icon}`}></i>
      </span>
      <span className="flex-grow-1" style={{ minWidth: 0 }}>
        <span className="st-tile-label d-block text-truncate">{item.label}</span>
        <span className="st-tile-desc d-block text-truncate">{item.desc}</span>
      </span>
      {disabled ? (
        <span className="st-badge flex-shrink-0">Coming soon</span>
      ) : (
        <i className="bi bi-chevron-right st-tile-arrow flex-shrink-0" aria-hidden="true"></i>
      )}
    </>
  );
  const className = `st-tile d-flex align-items-center gap-3 h-100 text-decoration-none${disabled ? " is-disabled" : ""}`;
  return item.to ? (
    <Link to={item.to} className={className}>{content}</Link>
  ) : (
    <div className={className} aria-disabled="true" title="Coming soon">{content}</div>
  );
};

const Setting = () => {
  const [query, setQuery] = useState("");

  const visibleSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS
      .map((s) => ({ ...s, items: s.items.filter((i) => `${i.label} ${i.desc}`.toLowerCase().includes(q)) }))
      .filter((s) => s.items.length > 0);
  }, [query]);

  return (
    <section className="section dashboard st-page">
      <style>{`
        .st-page { --st-border: #e3e7ef; --st-text: #0f172a; --st-muted: #64748b; --st-subtle: #94a3b8; --st-surface: #fff; --st-soft: #f8fafc;
                   --st-tint: color-mix(in srgb, var(--btn-bg) 9%, #fff); --st-ring: color-mix(in srgb, var(--btn-bg) 28%, transparent); }
        .st-title { font-size: 20px; font-weight: 600; color: var(--page-heading, var(--st-text)); letter-spacing: -0.01em; margin: 0; }
        .st-lead { font-size: 13px; color: var(--st-muted); margin: 4px 0 0; }
        .st-crumb { font-size: 12px; color: var(--st-muted); }
        .st-crumb a { color: var(--st-muted); text-decoration: none; }
        .st-crumb a:hover { color: var(--st-text); }
        .st-crumb a:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--st-ring); border-radius: 4px; }

        .st-search { position: relative; width: 280px; max-width: 100%; }
        .st-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--st-subtle); font-size: 13px; pointer-events: none; }
        .st-search input { height: 36px; width: 100%; padding: 0 12px 0 34px; font-size: 13px; color: var(--st-text); background: var(--st-surface);
                           border: 1px solid var(--st-border); border-radius: 8px; transition: border-color .15s, box-shadow .15s; }
        .st-search input::placeholder { color: var(--st-subtle); }
        .st-search input:focus { outline: none; border-color: var(--btn-bg); box-shadow: 0 0 0 3px var(--st-ring); }

        .st-card { background: var(--st-surface); border: 1px solid var(--st-border); border-radius: 12px; margin-bottom: 24px;
                   box-shadow: 0 1px 2px rgba(15, 23, 42, .04); }
        .st-card-head { padding: 16px 24px; border-bottom: 1px solid var(--st-border); }
        .st-card-title { font-size: 15px; font-weight: 600; color: var(--st-text); margin: 0; }
        .st-card-sub { font-size: 13px; color: var(--st-muted); margin: 2px 0 0; }
        .st-count { font-size: 12px; font-weight: 500; color: var(--st-muted); background: var(--st-soft); border: 1px solid var(--st-border);
                    border-radius: 999px; padding: 2px 10px; white-space: nowrap; }
        .st-card-body { padding: 24px; }

        .st-tile { background: var(--st-surface); border: 1px solid var(--st-border); border-radius: 10px; padding: 16px;
                   transition: border-color .15s, box-shadow .15s, background-color .15s; }
        .st-tile-icon { width: 40px; height: 40px; border-radius: 8px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
                        background: var(--st-tint); color: var(--btn-bg); font-size: 18px; }
        .st-tile-label { font-size: 14px; font-weight: 600; color: var(--st-text); line-height: 20px; }
        .st-tile-desc { font-size: 13px; color: var(--st-muted); line-height: 18px; margin-top: 2px; }
        .st-tile-arrow { font-size: 13px; color: var(--st-subtle); transition: color .15s, transform .15s; }
        .st-tile:not(.is-disabled):hover { border-color: #cbd2de; box-shadow: 0 4px 12px rgba(15, 23, 42, .06); }
        .st-tile:not(.is-disabled):hover .st-tile-arrow { color: var(--btn-bg); transform: translateX(2px); }
        .st-tile:focus-visible { outline: none; border-color: var(--btn-bg); box-shadow: 0 0 0 3px var(--st-ring); }
        .st-tile.is-disabled { background: var(--st-soft); cursor: not-allowed; }
        .st-tile.is-disabled .st-tile-icon { background: #eef1f5; color: var(--st-subtle); }
        .st-tile.is-disabled .st-tile-label { color: var(--st-muted); }
        .st-badge { font-size: 11px; font-weight: 500; color: var(--st-muted); background: var(--st-surface); border: 1px solid var(--st-border);
                    border-radius: 999px; padding: 2px 8px; white-space: nowrap; }

        .st-empty { text-align: center; padding: 48px 24px; }
        .st-empty-icon { width: 48px; height: 48px; border-radius: 50%; background: var(--st-soft); border: 1px solid var(--st-border);
                         display: inline-flex; align-items: center; justify-content: center; color: var(--st-subtle); font-size: 18px; margin-bottom: 12px; }
        .st-empty-btn { height: 36px; padding: 0 16px; font-size: 13px; font-weight: 500; border-radius: 8px; color: var(--st-text);
                        background: var(--st-surface); border: 1px solid var(--st-border); }
        .st-empty-btn:hover { background: var(--st-soft); }
        .st-empty-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--st-ring); }

        @media (max-width: 575.98px) {
          .st-search { width: 100%; }
          .st-card-head { padding: 16px; }
          .st-card-body { padding: 16px; }
        }
      `}</style>

      <header className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <nav aria-label="Breadcrumb" className="st-crumb d-flex align-items-center gap-2 mb-2">
            <Link to="/" aria-label="Home"><i className="bi bi-house-door"></i></Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true"></i>
            <span aria-current="page" style={{ color: "var(--st-text)", fontWeight: 500 }}>Settings</span>
          </nav>
          <h4 className="st-title">Settings</h4>
          <p className="st-lead">Manage master data and configuration used across the supplier panel.</p>
        </div>
        <div className="st-search" role="search">
          <i className="bi bi-search" aria-hidden="true"></i>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings"
            aria-label="Search settings"
          />
        </div>
      </header>

      {visibleSections.length === 0 ? (
        <div className="st-card st-empty">
          <span className="st-empty-icon"><i className="bi bi-search" aria-hidden="true"></i></span>
          <p className="st-card-title">No settings found</p>
          <p className="st-card-sub mb-3">Nothing matches "{query.trim()}". Try a different keyword.</p>
          <button type="button" className="st-empty-btn" onClick={() => setQuery("")}>Clear search</button>
        </div>
      ) : (
        visibleSections.map((section) => (
          <div key={section.title} className="st-card">
            <div className="st-card-head d-flex align-items-start justify-content-between gap-3">
              <div style={{ minWidth: 0 }}>
                <h6 className="st-card-title">{section.title}</h6>
                <p className="st-card-sub">{section.subtitle}</p>
              </div>
              <span className="st-count">{section.items.length} {section.items.length === 1 ? "item" : "items"}</span>
            </div>
            <div className="st-card-body">
              <div className="row g-3">
                {section.items.map((item) => (
                  <div key={item.label} className="col-12 col-md-6 col-xl-4">
                    <SettingTile item={item} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))
      )}
    </section>
  );
};

export default Setting;
